import { useState, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Typography } from 'antd';
import isEmpty from 'lodash/isEmpty';
import { dialogFunction, CustomNotification } from 'common/components';
import { QaSpecHelpContentGridLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';
import { useQaSpecGrid } from './useQaSpecGrid';
import useCheckSnapshotForRetailer from './useCheckSnapshotForRetailer';
import { useGetQaSpecRequiredFieldsInQaSpecFullView } from './useGetQaSpecRequiredFields';
import { useGetPackagingMasterCaseQuery } from 'pages/qa-spec/query';
import { sleep } from 'utils/delay';
import { apiHandler } from 'utils/api';
import { getAllRows } from 'pages/qa-spec/utils';
import { addErrorsToRow } from 'pages/qa-spec/utils';
import { checkSectionRequired } from 'pages/qa-spec/utils';
import { createTextEditorRenderer } from 'pages/qa-spec/utils/gridColumnUtils';
import {
  getProductSpecRequiredFields,
  validateAllRows,
  validatePackagingMasterCaseGrid,
  validateRequiredFieldProductSpec,
} from 'pages/qa-spec/components/qa-prod-spec/utils';
import { queryQaEvalFormKey } from 'pages/new-evaluation-form/hooks/useGetEvaluationForm';
import * as qaSpecServices from 'services/qaSpec';
import { useIntl } from 'react-intl';
import Messages from 'i18n/messages/qa-spec';
import dataSets from '../../qa-spec/data/packaging.json';
import { useDynamicLookups } from '../../qa-spec/hooks/useDynamicLookups';
import { numericKeyboardGuard } from '../utils/gridKeyboardUtils';
import { handleTabToNextEditableCell } from '../utils/gridTabUtils';
import { useGetProductEnums } from 'hooks';

export const useRetailerCase = ({ productId, enumOptions, isGridEditing }) => {
  const intl = useIntl();
  const gridInst = useRef();
  const queryClient = useQueryClient();
  const isToggleSnapshot = useCheckSnapshotForRetailer();
  const [saveLoading, setSaveLoading] = useState(false);
  const { data: requiredFields } = useGetQaSpecRequiredFieldsInQaSpecFullView({
    productId,
  });
  const isToggleRequiredFields = !isEmpty(requiredFields);
  const packagingRetailerUnitRequiredFields = getProductSpecRequiredFields({
    gridInst,
    requiredFields,
    requiredFieldPath: 'qaSpecification.qaSpecEprPackaging.retailerUnitComponents'.toLowerCase(),
  });
  const isSectionRequired = checkSectionRequired(requiredFields, 'retailerUnit');
  const resetGridCallback = () => {
    if (isToggleRequiredFields) {
      validateAllRows({
        gridInst,
        requiredFields: packagingRetailerUnitRequiredFields,
      });
    }
  };
  const { handleRefetchMasterCase } = useGetPackagingMasterCaseQuery({
    productId,
  });
  const { packagingData, isLoading, isFetching } = useGetPackagingMasterCaseQuery({
    productId,
    enabled: !!productId,
  });
  const transformedGridData = useMemo(() => {
    if (!packagingData?.retailerUnitComponents) return packagingData?.retailerUnitComponents;

    return packagingData.retailerUnitComponents.map(row => {
      const transformed = { ...row };

      transformed.packagingFunctionCode = transformed.packagingFunctionCode || [];
      transformed.packagingRecyclingProcessTypeCode = transformed.packagingRecyclingProcessTypeCode || [];

      // Reverse transform recyclable
      if (transformed.recyclable === "True" || transformed.recyclable === "Yes") {
        transformed.recyclable = 'Yes';
      } else if (transformed.recyclable === "False" || transformed.recyclable === "No") {
        transformed.recyclable = 'No';
      } else {
        transformed.recyclable = 'No';
      }

      return transformed;
    });
  }, [packagingData?.retailerUnitComponents]);
  const {
    rowData,
    addGridRow,
    selectedRow,
    selectGridRow,
    resetGridData,
    deleteSelectedGridRow,
  } = useQaSpecGrid({
    gridInst,
    isGridEditing,
    gridData: transformedGridData,
    resetGridCallback,
  });
  const fetchDataLoading = isLoading && isFetching;
  const { recyclableType, packagingType, materialUOM, packagingMaterial, loading: lookupsLoading } = useDynamicLookups({ demo: enumOptions?.lookupsDemo });
  const { productEnums } = useGetProductEnums();
  const packagingFunctionCodes = productEnums?.filter(x => x.enumName == "PackagingFunctionCodeEnum")[0]?.enumProperties?.filter(x => x.enumCode && x.enumDisplayName).map(x => ({value: x.enumDisplayName, displayName: x.enumDisplayName })) || [];
  const packagingRecyclingProcessTypeCodes = productEnums?.filter(x => x.enumName == "PackagingRecyclingProcessTypeCodeEnum")[0]?.enumProperties?.filter(x => x.enumCode && x.enumDisplayName).map(x => ({value: x.enumDisplayName, displayName: x.enumDisplayName })) || [];
  const columnDefs = useMemo(() => {
    const statusField = {
      minWidth: 50,
      field: 'error',
      displayName: ' ',
      cellRenderer: 'StatusRender',
      type: 'nonEditableColumn',
    };
    const textFromMultipleCodes = (codes, codeValuesDisplayNames) => (codes ?? []).map(x => codeValuesDisplayNames.find(y => y.value === x)?.displayName ?? x).join(', ');

    let cols = [
      {
        headerName: 'Component Number',
        field: 'displayNumber',
        dataType: 'number',
        minWidth: 200,
        allowFilter: true,
        allowSort: true,
        editable: false,
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        valueGetter: params => params.node.rowIndex + 1,
        tooltipValueGetter: params => params.node.rowIndex + 1,
      },
      {
        headerName: 'Unit',
        field: 'unit',
        editable: isGridEditing,
        cellEditor: 'agTextCellEditor',
        width: 80,
        allowFilter: true,
        allowSort: true,
        // validation logic
        valueSetter: params => {
          const raw = params.newValue;

          // treat empty as null
          if (raw === '' || raw == null) {
            params.data.unit = null;
            return true;
          }

          const v = parseInt(raw, 10);
          if (isNaN(v)) {
            params.data.unit = null;
            return true;
          }

          if (v < 1) return false;

          params.data.unit = v;
          return true;
        },

        onCellEditingStopped: params => {
          const v = parseInt(params.value, 10);
          if (isNaN(v) || v < 1) {
            params.api.showTooltip({
              rowIndex: params.rowIndex,
              colKey: params.column.getId()
            })
          }
        },

        suppressKeyboardEvent: params =>
          numericKeyboardGuard({ params }),

        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.qaSpecEprPackaging.retailerUnitComponents.unit',
        },
        // red border if invalid
        cellClassRules: {
          'cell-error': params => {
            const value = params.value

            // optional field → empty is valid
            if (value == null || value === '') return false

            const v = parseInt(value, 10);
            return isNaN(v) || v < 1
          }
        },

        // tooltip if invalid
        tooltipValueGetter: params => params.value,
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Packaging Type',
        field: 'packagingType',
        flex: null,
        valueFormatter: params => {
          const option = packagingType.find(o => o.code === params.value);
          return option ? option.displayName : params.value;
        },
        cellEditorSelector: () => {
          return {
            component: 'SelectionEditor',
            params: {
              values: packagingType.map((item) => ({
                value: item.code,
                displayName: item.displayName,
              })),
              mapValue: (value) => {
                if (value === 'null' || value === undefined) {
                  return null;
                } else {
                  return value;
                }
              },
              popup: true,
            },
          };
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : null,
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.qaSpecEprPackaging.retailerUnitComponents.packagingType',
        },
        tooltipValueGetter: params => {
          const option = packagingType.find(o => o.code === params.value);
          return option ? option.displayName : params.value;
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Packaging Material',
        field: 'packagingMaterial',
        flex: null,
        valueFormatter: params => {
          const option = packagingMaterial.find(o => o.code === params.value);
          return option ? option.displayName : params.value;
        },
        cellEditorSelector: () => {
          return {
            component: 'SelectionEditor',
            params: {
              values: packagingMaterial.map((item) => ({
                value: item.code,
                displayName: item.displayName,
              })),
              mapValue: (value) => {
                if (value === 'null' || value === undefined) {
                  return null;
                } else {
                  return value;
                }
              },
              popup: true,
            },
          };
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : null,
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.qaSpecEprPackaging.retailerUnitComponents.packagingMaterial',
        },
        tooltipValueGetter: params => {
          const option = packagingMaterial.find(o => o.code === params.value);
          return option ? option.displayName : params.value;
        },
      },
      {
        headerName: 'Material Function',
        dataType: 'string',
        field: 'packagingFunctionCode',
        editable: isGridEditing,
        headerComponent: QaSpecHelpContentGridLabel,
        flex: null,
        headerComponentParams: { fieldPath: 'qaSpecification.qaSpecEprPackaging.retailerUnitComponents.packagingFunctionCode' },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : null,
        valueFormatter: params => textFromMultipleCodes(params.value, packagingFunctionCodes),
        tooltipValueGetter: params => textFromMultipleCodes(params.value, packagingFunctionCodes),
        cellClass: 'ag-cell-has-multi-select',
        cellEditorSelector: () =>  ({
          component: 'SelectionEditor',
          params: {
            values: packagingFunctionCodes,
            mode: 'multiple',
          },
        }),
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Material Description',
        field: 'materialDescription',
        width: 200,
        flex: null,
        cellEditorSelector: createTextEditorRenderer(500),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.qaSpecEprPackaging.retailerUnitComponents.materialDescription',
        },
        tooltipValueGetter: params => params.value,
      },
      {
        headerName: 'Material Weight',
        field: 'materialWeight',
        editable: isGridEditing,
        cellEditor: 'agNumberCellEditor',
        // validation logic
        valueSetter: params => {
          const raw = params.newValue;

          // treat empty as null
          if (raw === '' || raw == null) {
            params.data.materialWeight = null;
            return true;
          }

          const v = Number(raw);
          if (Number.isNaN(v)) {
            params.data.materialWeight = null;
            return true;
          }

          if (v <= 0) return false;

          // Check if more than 2 decimal places
          const decimalPart = String(v).split('.')[1];
          if (decimalPart && decimalPart.length > 4) return false;

          params.data.materialWeight = v;
          return true;
        },

        onCellEditingStopped: params => {
          const v = Number(params.value);
          const decimalPart = String(v).split('.')[1];
          if (v <= 0 || (decimalPart && decimalPart.length > 4)) {
            params.api.showTooltip({
              rowIndex: params.rowIndex,
              colKey: params.column.getId()
            })
          }
        },
        minWidth: 180,
        allowFilter: true,
        allowSort: true,

        onCellKeyDown: params =>
          handleTabToNextEditableCell({
            params,
            nextColKey: 'materialUOM',
          }),

        suppressKeyboardEvent: params =>
          numericKeyboardGuard({
            params,
            allowDecimal: true,
          }),

        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.qaSpecEprPackaging.retailerUnitComponents.materialWeight',
        },
        // red border if invalid
        cellClassRules: {
          'cell-error': params => {
            const value = params.value

            // optional field → empty is valid
            if (value == null || value === '') return false

            const v = Number(value);
            const decimalPart = String(v).split('.')[1];
            return v <= 0 || (decimalPart && decimalPart.length > 4);
          }
        },

        // tooltip if invalid
        tooltipValueGetter: params => params.value,
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Material Weight UOM',
        field: 'materialUOM',
        flex: null,
        minWidth: 220,
        cellEditorSelector: () => {
          return {
            component: 'SelectionEditor',
            params: {
              values: dataSets.materialUOM,
              mapValue: (value) => {
                if (value === 'null' || value === undefined) {
                  return null;
                } else {
                  return value;
                }
              },
              popup: true,
            },
          };
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.qaSpecEprPackaging.retailerUnitComponents.materialUOM',
        },
        tooltipValueGetter: params => params.value,
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Recyclable',
        field: 'recyclable',
        cellEditorSelector: () => {
          return {
            component: 'SelectionEditor',
            params: {
              values: [
                { value: 'Yes', displayName: 'Yes' },
                { value: 'No', displayName: 'No' },
              ],
              mapValue: (value) => {
                if (value === 'null' || value === undefined) {
                  return null;
                } else {
                  return value;
                }
              },
              popup: true,
            },
          };
        },
        onCellValueChanged: params => {
          // clear recyclableType when recyclable is not 'Yes'
          const newVal = params.newValue ?? params.value;
          if (newVal !== 'Yes') {
            try {
              params.node.setDataValue('recyclableType', null);
            } catch (e) {
              if (params.data) params.data.recyclableType = null;
            }
          }
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.qaSpecEprPackaging.retailerUnitComponents.recyclable',
        },
        tooltipValueGetter: params => params.value,
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Recyclable Type',
        minWidth: 186,
        field: 'recyclableType',
        valueFormatter: params => {
          const option = recyclableType.find(o => o.code === params.value);
          return option ? option.displayName : params.value;
        },
        cellEditorSelector: () => {
          return {
            component: 'SelectionEditor',
            params: {
              values: recyclableType.map((item) => ({
                value: item.code,
                displayName: item.displayName,
              })),
              mapValue: (value) => {
                if (value === 'null' || value === undefined) {
                  return null;
                } else {
                  return value;
                }
              },
              popup: true,
            },
          };
        },
        // editable only if recyclable is 'Yes'
        editable: params => isGridEditing && params?.data?.recyclable === 'Yes',
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : null,
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.qaSpecEprPackaging.retailerUnitComponents.recyclableType',
        },
        // visually indicate disabled state and show tooltip when disabled
        cellClassRules: {
          'cell-disabled': params => params?.data?.recyclable !== 'Yes',
        },
        tooltipValueGetter: params => {
          const option = recyclableType.find(o => o.code === params.value);
          return option ? option.displayName : params.value;
        },
      },
      {
        headerName: 'Recycling Process',
        dataType: 'string',
        field: 'packagingRecyclingProcessTypeCode',
        editable: isGridEditing,
        headerComponent: QaSpecHelpContentGridLabel,
        flex: null,
        headerComponentParams: { fieldPath: 'qaSpecification.qaSpecEprPackaging.retailerUnitComponents.packagingRecyclingProcessTypeCode' },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : null,
        valueFormatter: params => textFromMultipleCodes(params.value, packagingRecyclingProcessTypeCodes),
        tooltipValueGetter: params => textFromMultipleCodes(params.value, packagingRecyclingProcessTypeCodes),
        cellClass: 'ag-cell-has-multi-select',
        cellEditorSelector: () =>  ({
          component: 'SelectionEditor',
          params: {
            values: packagingRecyclingProcessTypeCodes,
            mode: 'multiple',
          },
        }),
      },
      {
        headerName: 'Total Recycled Content Percentage',
        field: 'totalRecycledContentPercentage',
        editable: isGridEditing,
        cellEditor: 'agNumberCellEditor',
        // validation logic
        valueSetter: params => {
          const raw = params.newValue;

          // treat empty as null
          if (raw === '' || raw == null) {
            params.data.totalRecycledContentPercentage = null;
            return true;
          }

          const v = Number(raw);
          if (Number.isNaN(v)) {
            params.data.totalRecycledContentPercentage = null;
            return true;
          }

          if (v < 0 || v > 100) return false;

          // Check if more than 2 decimal places
          const decimalPart = String(v).split('.')[1];
          if (decimalPart && decimalPart.length > 2) return false;

          params.data.totalRecycledContentPercentage = v;
          return true;
        },

        onCellEditingStopped: params => {
          const v = Number(params.value)
          const decimalPart = String(v).split('.')[1];
          if (v < 0 || v > 100 || (decimalPart && decimalPart.length > 2)) {
            params.api.showTooltip({
              rowIndex: params.rowIndex,
              colKey: params.column.getId()
            })
          }
        },
        minWidth: 300,
        allowFilter: true,
        allowSort: true,

        suppressKeyboardEvent: params =>
          numericKeyboardGuard({
            params,
            allowDecimal: true,
          }),

        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.qaSpecEprPackaging.retailerUnitComponents.totalRecycledContentPercentage',
        },
        // red border if invalid
        cellClassRules: {
          'cell-error': params => {
            const value = params.value

            // optional field → empty is valid
            if (value == null || value === '') return false

            const v = Number(value)
            const decimalPart = String(v).split('.')[1];
            return v < 0 || v > 100 || (decimalPart && decimalPart.length > 2)
          }
        },

        // tooltip if invalid
        tooltipValueGetter: params => {
          const v = Number(params.value);
          const decimalPart = String(v).split('.')[1];
          if (v < 0 || v > 100)
            return 'Value must be greater than equal to 0 and not exceed 100';
          if (decimalPart && decimalPart.length > 2) return 'Maximum 2 decimal places allowed';
          return params.value;
        },
      },
      {
        headerName: 'Pre-Consumer Recycled Content Percentage',
        field: 'preConsumerRecycledContentPercentage',
        editable: isGridEditing,
        cellEditor: 'agNumberCellEditor',
        // validation logic
        valueSetter: params => {
          const raw = params.newValue;

          // treat empty as null
          if (raw === '' || raw == null) {
            params.data.preConsumerRecycledContentPercentage = null;
            return true;
          }

          const v = Number(raw);
          if (Number.isNaN(v)) {
            params.data.preConsumerRecycledContentPercentage = null;
            return true;
          }

          if (v < 0 || v > 100) return false;

          // Check if more than 2 decimal places
          const decimalPart = String(v).split('.')[1];
          if (decimalPart && decimalPart.length > 2) return false;

          params.data.preConsumerRecycledContentPercentage = v;
          return true;
        },

        onCellEditingStopped: params => {
          const v = Number(params.value)
          const decimalPart = String(v).split('.')[1];
          if (v < 0 || v > 100 || (decimalPart && decimalPart.length > 2)) {
            params.api.showTooltip({
              rowIndex: params.rowIndex,
              colKey: params.column.getId()
            })
          }
        },
        minWidth: 360,
        allowFilter: true,
        allowSort: true,

        suppressKeyboardEvent: params =>
          numericKeyboardGuard({
            params,
            allowDecimal: true,
          }),

        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.qaSpecEprPackaging.retailerUnitComponents.preConsumerRecycledContentPercentage',
        },
        // red border if invalid
        cellClassRules: {
          'cell-error': params => {
            const value = params.value

            // optional field → empty is valid
            if (value == null || value === '') return false

            const v = Number(value)
            const decimalPart = String(v).split('.')[1];
            return v < 0 || v > 100 || (decimalPart && decimalPart.length > 2)
          }
        },

        // tooltip if invalid
        tooltipValueGetter: params => {
          const v = Number(params.value);
          const decimalPart = String(v).split('.')[1];
          if (v < 0 || v > 100)
            return 'Value must be greater than equal to 0 and not exceed 100';
          if (decimalPart && decimalPart.length > 2) return 'Maximum 2 decimal places allowed';
          return params.value;
        },
      },
      {
        headerName: 'Post-Consumer Recycled Content Percentage',
        field: 'postConsumerRecycledContentPercentage',
        editable: isGridEditing,
        cellEditor: 'agNumberCellEditor',

        // validation logic
        valueSetter: params => {
          const raw = params.newValue;

          // treat empty as null
          if (raw === '' || raw == null) {
            params.data.postConsumerRecycledContentPercentage = null;
            return true;
          }

          const v = Number(raw);
          if (Number.isNaN(v)) {
            params.data.postConsumerRecycledContentPercentage = null;
            return true;
          }

          if (v < 0 || v > 100) return false;

          // Check if more than 2 decimal places
          const decimalPart = String(v).split('.')[1];
          if (decimalPart && decimalPart.length > 2) return false;

          params.data.postConsumerRecycledContentPercentage = v;
          return true;
        },

        onCellEditingStopped: params => {
          const v = Number(params.value)
          const decimalPart = String(v).split('.')[1];
          if (v < 0 || v > 100 || (decimalPart && decimalPart.length > 2)) {
            params.api.showTooltip({
              rowIndex: params.rowIndex,
              colKey: params.column.getId()
            })
          }
        },
        minWidth: 360,
        allowFilter: true,
        allowSort: true,

        suppressKeyboardEvent: params =>
          numericKeyboardGuard({
            params,
            allowDecimal: true,
          }),


        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.qaSpecEprPackaging.retailerUnitComponents.postConsumerRecycledContentPercentage',
        },

        // red border if invalid
        cellClassRules: {
          'cell-error': params => {
            const value = params.value

            // optional field → empty is valid
            if (value == null || value === '') return false

            const v = Number(value)
            const decimalPart = String(v).split('.')[1];
            return v < 0 || v > 100 || (decimalPart && decimalPart.length > 2)
          }
        },

        // tooltip if invalid
        tooltipValueGetter: params => {
          const v = Number(params.value);
          const decimalPart = String(v).split('.')[1];
          if (v < 0 || v > 100)
            return 'Value must be greater than equal to 0 and not exceed 100';
          if (decimalPart && decimalPart.length > 2) return 'Maximum 2 decimal places allowed';
          return params.value;
        },
      },
    ];

    cols = isToggleRequiredFields ? [statusField, ...cols] : cols;

    return cols.map((fieldItem) => {
      const DEFAULT_WIDTH = 150;

      let initColSetting = {
        type: 'editableColumn',
      };

      return {
        minWidth: DEFAULT_WIDTH,
        ...initColSetting,
        ...fieldItem,
      };
    });
  }, [enumOptions, isToggleSnapshot, packagingType, packagingMaterial, materialUOM, recyclableType, packagingFunctionCodes, packagingRecyclingProcessTypeCodes, isGridEditing]);

  const cancelEdit = () => {
    resetGridData();
  };
  const addNewRow = () => {
    const currentRows = gridInst.current?.api.getRenderedNodes() || [];
    const maxComponentNumberId = currentRows.reduce((max, row) => {
      return Math.max(max, row.data.componentNumberId || 0);
    }, 0);
    const newComponentNumberId = maxComponentNumberId + 1;
    let newRowData = {
      displayNumber: newComponentNumberId,
      componentNumberId: null,
      unit: null,
      packagingType: null,
      packagingMaterial: null,
      packagingFunctionCode: [],
      materialDescription: null,
      materialWeight: null,
      materialUOM: 'Pound',
      recyclable: null,
      recyclableType: null,
      packagingRecyclingProcessTypeCode: [],
      totalRecycledContentPercentage: null,
      preConsumerRecycledContentPercentage: null,
      postConsumerRecycledContentPercentage: null,
    };

    if (isToggleRequiredFields) {
      const errorRequiredFields = packagingRetailerUnitRequiredFields?.map(
        (field) => field?.errorMessage
      );
      newRowData = addErrorsToRow(newRowData, errorRequiredFields);
      addGridRow(newRowData);
      // Trigger validation to clear errors for fields with default values
      setTimeout(() => {
        validateAllRows({
          gridInst,
          requiredFields: packagingRetailerUnitRequiredFields,
        });
      }, 0);
      return;
    }

    addGridRow(newRowData);
  };
  const saveData = async (toggleEditMode, analysisNotExecuted) => {
    gridInst.current.api.stopEditing();

    const GRID_VALIDATION_DELAY = 700;
    await sleep(GRID_VALIDATION_DELAY);

    // Check for cell-level validation errors (red borders)
    const hasCellErrors = gridInst.current?.api?.forEachNode((node) => {
      const cells = node.childrenAfterFilter || [node];
      for (let cell of cells) {
        if (cell.childrenAfterFilter) continue;
        const cellElement = document.querySelector(
          `[data-row-index="${node.rowIndex}"] [col-id]`
        );
        if (cellElement?.classList?.contains('cell-error')) {
          return true;
        }
      }
      return false;
    });

    // Alternative: Check rendered DOM for cell-error class
    const errorCells = document.querySelectorAll(
      '[role="gridcell"].cell-error'
    );
    if (errorCells?.length > 0) {
      CustomNotification.error(
        `Please fix the grid validation errors before saving.`
      );
      return;
    }

    // const isGridHaveError = validatePackagingMasterCaseGrid({
    //   grid: gridInst,
    // }).checkIfGridHaveError();

    // if (isGridHaveError) {
    //   CustomNotification.error(
    //     `Please fill the required fields.`
    //   );
    //   return;
    // }
    const allRowData = getAllRows(gridInst);

    const transformedComponents = allRowData.map(row => {
      const transformed = { ...row };

      // Transform packagingType
      if (transformed.packagingType) {
        const option = packagingType.find(o => o.code === transformed.packagingType);
        transformed.packagingType = option ? option.displayName : transformed.packagingType;
      }

      // Transform packagingMaterial
      if (transformed.packagingMaterial) {
        const option = packagingMaterial.find(o => o.code === transformed.packagingMaterial);
        transformed.packagingMaterial = option ? option.displayName : transformed.packagingMaterial;
      }

      // Transform materialUOM
      if (transformed.materialUOM) {
        const option = materialUOM.find(o => o.code === transformed.materialUOM);
        transformed.materialUOM = option ? option.displayName : transformed.materialUOM;
      }

      // Transform recyclableType
      if (transformed.recyclableType) {
        const option = recyclableType.find(o => o.code === transformed.recyclableType);
        transformed.recyclableType = option ? option.displayName : transformed.recyclableType;
      }

      // Transform recyclable
      if (transformed.recyclable === 'Yes') {
        transformed.recyclable = "True";
      } else if (transformed.recyclable === 'No') {
        transformed.recyclable = "False";
      } else {
        transformed.recyclable = null;
      }

      return transformed;
    });

    if (
      isToggleRequiredFields &&
      packagingRetailerUnitRequiredFields?.length > 0 &&
      allRowData?.length <= 0
    ) {
      CustomNotification.error('Please add at least one row data to save');
      return;
    }

    selectGridRow(null);

    setSaveLoading(true);

    const params = {
      productId,
      components: transformedComponents
    };

    const successMessage = intl.formatMessage(
      Messages.productSpecSaveRetailerUnit
    );
    const errorMessage = intl.formatMessage(
      Messages.productSpecSaveRetailerUnitError
    );

    apiHandler({
      service: qaSpecServices.saveQaSpecPackagingRetailerUnit,
      params,
      successMessage,
      errorMessage,
      successCallback: saveSuccessCallback(toggleEditMode),
      onFinally: saveFinally,
    });
  };
  const saveSuccessCallback = (toggleEditMode) => () => {
    toggleEditMode();

    handleRefetchMasterCase();

    queryClient.invalidateQueries({
      queryKey: queryQaEvalFormKey(productId, 'Food'),
    });
  };
  const saveFinally = () => {
    setSaveLoading(false);
  };
  const deleteData = (toggleEditMode) => {
    dialogFunction({
      type: 'warn',
      content: 'Are you sure you want to delete the selected item?',
      okText: 'OK',
      cancelText: 'Cancel',
      onOk: () => onDeleteData(toggleEditMode),
    });
  };
  const onDeleteData = (toggleEditMode) => {
    gridInst.current.api.stopEditing();

    setSaveLoading(true);

    const params = {
      productId,
      componentNumberId: selectedRow?.data?.componentNumberId,
      componentDescription: selectedRow?.data?.componentDescription
    };

    const successMessage = intl.formatMessage(
      Messages.productSpecDeleteRetailerUnit
    );
    const errorMessage = intl.formatMessage(
      Messages.productSpecDeleteRetailerUnitError
    );

    apiHandler({
      service: qaSpecServices.deleteQaSpecRetailerUnitPackagingComponent,
      params,
      successMessage,
      errorMessage,
      successCallback: deleteSuccessCallback(toggleEditMode),
      onFinally: saveFinally,
    });
  };
  const deleteSuccessCallback = (toggleEditMode) => () => {
    deleteSelectedGridRow();
    selectGridRow(null);

    if (typeof toggleEditMode === 'function') {
      try {
        toggleEditMode();
      } catch (err) {
        console.error('toggleEditMode threw an error in deleteSuccessCallback:', err);
      }
    }

    handleRefetchMasterCase();

    queryClient.invalidateQueries({
      queryKey: queryQaEvalFormKey(productId, 'Food'),
    });
  };
  const selectRow = () => {
    selectGridRow();
  };
  const handleValidateRequiredFieldForSupplier = ({ event }) => {
    packagingRetailerUnitRequiredFields?.forEach((item) => {
      if (item?.fieldName === event.column.colId)
        validateRequiredFieldProductSpec({
          gridInst,
          event,
          requiredFieldError: item.errorMessage,
        });
    });
  };
  const onCellEditingStopped = async (event) => {
    if (isToggleRequiredFields) {
      handleValidateRequiredFieldForSupplier({
        event,
      });
    }
  };

  useEffect(() => {
    const handleValidateAllRowDataForSupplier = () => {
      if (rowData && rowData?.length > 0 && isToggleRequiredFields) {
        validateAllRows({
          gridInst,
          requiredFields: packagingRetailerUnitRequiredFields,
        });
      }
    };

    handleValidateAllRowDataForSupplier();
  }, [
    isToggleRequiredFields,
    packagingRetailerUnitRequiredFields,
    JSON.stringify(rowData),
  ]);

  const gridTitle = (
    <>
      {isToggleRequiredFields && isSectionRequired && (
        <>
          <Typography.Text type='danger'>*</Typography.Text>{' '}
        </>
      )}
      Retail Unit
    </>
  );

  return {
    gridInst,
    rowData,
    columnDefs,
    addNewRow,
    saveData,
    cancelEdit,
    deleteData,
    selectedRow,
    selectRow,
    loading: fetchDataLoading || saveLoading || lookupsLoading,
    onCellEditingStopped,
    isToggleRequiredFields,
    gridTitle,
  };
};